import { Citation } from "../shared/citation";
import { RevealTarget } from "../shared/editInfo";
import { columnIndex, parseQualified, qualify } from "./a1";
import { selectRange } from "./rangeTools";
import { SheetModel, WorkbookModel } from "./workbookModel";

// Citations in Excel are just addresses, which makes this the shortest citation
// implementation of the three hosts.
//
// Word has to lay down a bookmark at enrich time and navigate to that instead of the
// paragraph, because a paragraph ID goes stale the moment the document shifts. An
// address does not go stale, so there is nothing to create beforehand and nothing to
// clean up after -- revealing a citation is one select() call.
//
// There is one thing to do beforehand, though, and it is what enrich exists for here:
// QUALIFY. "B7" is stable, but "the active sheet" is not -- it is whatever sheet the
// user is looking at when they click, which is not necessarily the one that was active
// when the model wrote the citation, and certainly not after the previous citation
// took them somewhere else. An unqualified citation therefore selects the right cell
// on the wrong sheet, and the sheet never changes. So every citation is qualified
// against the SNAPSHOT the model was given, once, here -- after which a citation names
// exactly one cell in the workbook and reveal always has a sheet to activate.

/**
 * Matches the addresses the system prompt tells the model to emit inside
 * `<segment=...>`: an optional sheet prefix, then a cell or a range.
 *
 * Deliberately not `[^<>]+`. A permissive pattern would turn any stray angle-bracketed
 * text in the model's prose into a citation, and a citation that resolves to nothing
 * is worse than no citation -- it puts a clickable marker in front of the user that
 * does not go anywhere.
 */
export const EXCEL_SEGMENT_ID_PATTERN =
  "(?:[^<>!]+!)?\\$?[A-Za-z]{1,3}\\$?\\d+(?::\\$?[A-Za-z]{1,3}\\$?\\d+)?";

const truncate = (text: string, limit: number) =>
  text.length > limit ? text.substring(0, limit - 3) + "..." : text;

/**
 * Labels each citation with what is actually in the cell, so the reference footer
 * reads "Revenue: 42,500" rather than "Sheet1!D12".
 *
 * The model is read first and the workbook only for what the model does not cover --
 * a citation into a sampled sheet's omitted rows will not be in `rows`, and reading it
 * is a round trip worth making for a handful of citations but not for hundreds.
 */
export const enrichCitations = async (
  citations: Citation[],
  docModel: WorkbookModel
): Promise<void> => {
  if (citations.length === 0) return;

  const unresolved: Citation[] = [];
  const activeSheet = docModel.sheets.find((sheet) => sheet.isActive)?.name;

  for (const citation of citations) {
    citation.segmentId = qualifySegmentId(citation.segmentId, activeSheet);
    // The fallback, for a citation nothing better can be said about: the address, but
    // written for a person rather than for a formula.
    citation.label = displayAddress(citation.segmentId);
    const label = labelFromModel(citation.segmentId, docModel);
    if (label) {
      citation.label = label;
    } else {
      unresolved.push(citation);
    }
  }

  // A cap, because each of these is a round trip and a model that cites forty cells in
  // one answer should not cost forty syncs. The rest keep their address as the label,
  // which is still a working citation -- it just reads less well.
  const toRead = unresolved.slice(0, 15);
  if (toRead.length === 0) return;

  try {
    await Excel.run(async (context) => {
      const pending = toRead.map((citation) => {
        const { sheetName, address } = parseQualified(citation.segmentId);
        const sheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getRange(address);
        range.load("values");
        return { citation, range };
      });

      await context.sync();

      for (const { citation, range } of pending) {
        const values = (range.values ?? []) as unknown[][];
        const flat = values.flat().filter((value) => value !== null && value !== "");
        if (flat.length > 0) {
          citation.label = truncate(
            `${displayAddress(citation.segmentId)}: ${flat.slice(0, 4).join(", ")}`,
            80
          );
        }
      }
    });
  } catch {
    // A bad address from the model is the likely cause, and it is not worth failing the
    // whole answer over. The citations keep their addresses as labels and still work.
  }
};

/**
 * Binds an unqualified address to the sheet that was active when the model saw the
 * workbook, so it still means the same cell after the user (or an earlier citation
 * click) has moved to another sheet.
 *
 * Already-qualified addresses are returned untouched rather than re-qualified, and a
 * snapshot with no active sheet leaves the address alone -- resolving late against the
 * live active sheet is wrong, but it is better than resolving against nothing.
 */
function qualifySegmentId(segmentId: string, activeSheetName: string | undefined): string {
  const { sheetName, address } = parseQualified(segmentId);
  if (sheetName || !activeSheetName) return segmentId;
  return qualify(activeSheetName, address);
}

/** The label for an address already present in the extracted model, or null. */
function labelFromModel(segmentId: string, docModel: WorkbookModel): string | null {
  const { sheetName, address } = parseQualified(segmentId);

  // A range citation is labelled by its own address plus context from the sheet, since
  // there is no single value to show.
  const single = /^\$?([A-Za-z]{1,3})\$?(\d+)$/.exec(address);

  const sheet = sheetName
    ? docModel.sheets.find((candidate) => candidate.name === sheetName)
    : docModel.sheets.find((candidate) => candidate.isActive);
  if (!sheet) return null;

  if (!single) return truncate(labelRange(sheet, address), 80);

  const rowNumber = Number(single[2]);
  const row = sheet.rows.find((candidate) => candidate.row === rowNumber);
  if (!row) return null;

  const columnOffset = columnOffsetWithin(sheet.usedRange, single[1]);
  if (columnOffset === null || columnOffset >= row.cells.length) return null;

  const value = row.cells[columnOffset];
  if (value === null || value === "") return null;

  const header = sheet.headers?.[columnOffset];
  const prefix = header ? `${header}: ` : "";
  return truncate(`${prefix}${String(value)}`, 80);
}

/**
 * "Summary!A11:B12" is the model's address and a formula's, not a person's: it names a
 * rectangle, not what is in it, and the "!" is syntax. A range is labelled by what the
 * reader would call it instead -- the sheet, the columns by their headers, and the rows,
 * which are what tell one citation of a sheet from the next:
 *
 *   Summary · Item, Revenue · rows 11–12
 *   Tool matrix · Tool – Owner · rows 1–74     (more than three columns: first – last)
 *
 * Without headers the columns are left as letters, and anything that is not a plain
 * rectangle (whole rows or columns, a defined name) falls back to the address.
 */
function labelRange(sheet: SheetModel, address: string): string {
  const rectangle = /^\$?([A-Za-z]{1,3})\$?(\d+):\$?([A-Za-z]{1,3})\$?(\d+)$/.exec(address);
  if (!rectangle) return `${sheet.name} · ${address}`;

  const [, fromColumn, fromRow, toColumn, toRow] = rectangle;
  const rows = fromRow === toRow ? `row ${fromRow}` : `rows ${fromRow}–${toRow}`;

  const first = columnOffsetWithin(sheet.usedRange, fromColumn);
  const last = columnOffsetWithin(sheet.usedRange, toColumn);
  const headers =
    first === null || last === null
      ? []
      : (sheet.headers ?? []).slice(Math.max(first, 0), last + 1).filter((h) => h.trim());

  const columns =
    headers.length === 0
      ? fromColumn === toColumn
        ? `column ${fromColumn.toUpperCase()}`
        : `columns ${fromColumn.toUpperCase()}–${toColumn.toUpperCase()}`
      : headers.length <= 3
        ? headers.join(", ")
        : `${headers[0]} – ${headers[headers.length - 1]}`;

  return `${sheet.name} · ${columns} · ${rows}`;
}

/** "'Q1 Actuals'!B7" as "Q1 Actuals · B7": the address without the formula syntax. */
function displayAddress(segmentId: string): string {
  const { sheetName, address } = parseQualified(segmentId);
  return sheetName ? `${sheetName} · ${address}` : address;
}

/** Where a column sits relative to the used range's first column. */
function columnOffsetWithin(usedRange: string | null, columnLetters: string): number | null {
  if (!usedRange) return null;
  const firstColumn = /^([A-Za-z]{1,3})/.exec(usedRange);
  if (!firstColumn) return null;

  try {
    return columnIndex(columnLetters) - columnIndex(firstColumn[1]);
  } catch {
    // columnIndex throws on anything that is not a column reference, which here means
    // the model cited something malformed. Not worth failing an answer over.
    return null;
  }
}

/** Scroll to a citation and select it. */
export const reveal = async (target: RevealTarget): Promise<void> => {
  // The segment id IS the address, for a citation and for an edit alike, so there is
  // nothing to resolve first.
  await selectRange(target.segmentId);
};
